// eslint-disable-next-line no-restricted-imports
import Checkbox from '@mui/material/Checkbox';
// eslint-disable-next-line no-restricted-imports
import FormControl from '@mui/material/FormControl';
// eslint-disable-next-line no-restricted-imports
import FormControlLabel from '@mui/material/FormControlLabel';
// eslint-disable-next-line no-restricted-imports
import FormGroup from '@mui/material/FormGroup';
// eslint-disable-next-line no-restricted-imports
import FormHelperText from '@mui/material/FormHelperText';
import { Form, Formik, FormikProps } from 'formik';
import { matchIsValidTel } from 'mui-tel-input';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Stack } from 'src/components/Stack';
import { StickyCard } from 'src/components/StickyCard';
import { Typography } from 'src/components/Typography';
import { FormPhoneInput } from 'src/shared/Forms/Fields/FormPhoneInput';
import { FormTextField } from 'src/shared/Forms/Fields/FormTextField';
import {
  postalCodeFormats,
  validatePostalCode,
} from 'src/utils/addressValidator';
import { validateURL } from 'src/utils/urlValidator';
import * as Yup from 'yup';

export interface SMSSetupFormValues {
  businessName: string;
  websiteUrl: string;
  businessType: string;
  businessIdType: string;
  businessId: string;
  regionsOfOperation: string[];
  jobTitle: string;
  jobPosition: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  streetAddress: string;
  streetAddress2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  areaCode: string;
  areaCodeTwo?: string;
}

// Function to get the expected ID type and label
const getExpectedIdInfo = (
  country?: string,
  businessType?: string,

  _t?: any // Optional translation function, not used currently
): { type: string; label: string } => {
  if (!country || !businessType)
    return { type: '', label: 'Business Registration ID' };

  switch (country) {
    case 'USA':
      if (businessType === 'Sole Proprietorship') {
        return { type: 'SSN', label: 'Social Security Number (SSN)' };
      }
      return {
        type: 'Employer Identification Number (EIN)',
        label: 'Employer Identification Number (EIN)',
      };
    case 'Canada':
      return {
        type: 'Canadian Business Number',
        label: 'Canadian Business Number',
      };
    case 'United Kingdom':
      return {
        type: 'Company Number (Great Britain)',
        label: 'Company Number (Great Britain)',
      };
    case 'Australia':
      if (businessType === 'Corporation') {
        return {
          type: 'Company Number from ASIC (ACN)',
          label: 'Company Number from ASIC (ACN)',
        };
      }
      // Default to ABN for all other business types (more common)
      return {
        type: 'Australian Business Number (ABN)',
        label: 'Australian Business Number (ABN)',
      };
    case 'New Zealand': // Added New Zealand
      return { type: 'NZBN', label: 'New Zealand Business Number (NZBN)' };
    case 'Luxembourg': // Added Luxembourg
      return { type: 'RCS', label: 'RCS Number' };
    case 'Denmark': // Added Denmark
      return { type: 'CVR', label: 'CVR Number' };
    case 'Switzerland': // Added Switzerland
      return { type: 'UID', label: 'Unternehmens-Identifikationsnummer (UID)' };
    case 'Netherlands': // Added Netherlands
      return { type: 'KvK Number', label: 'KvK Number (Chamber of Commerce)' };
    case 'Germany': // Added Germany
      return {
        type: 'Handelsregisterauszug',
        label: 'Handelsregisterauszug (Commercial Register Excerpt)',
      };
    // Italy and Hong Kong removed
    default:
      if (businessType === 'Sole Proprietorship') {
        return { type: '', label: 'Business Registration ID' };
      }
      return {
        type: 'Employer Identification Number (EIN)',
        label: 'Employer Identification Number (EIN)',
      };
  }
};

// Updated function to get helper text based on ID type
const getBusinessIdHelperText = (businessIdType?: string, t?: any): string => {
  if (!t) {
    // Fallback texts if translation function is not provided
    switch (businessIdType) {
      case 'SSN':
        return 'Format: XXX-XX-XXXX';
      case 'Employer Identification Number (EIN)':
        return 'Format: XX-XXXXXXX';
      case 'Canadian Business Number':
        return 'Format: 9 digits';
      case 'Company Number (Great Britain)':
        return 'Format: 8 alphanumeric characters';
      case 'Company Number from ASIC (ACN)':
        return 'Format: 9 digits';
      case 'Australian Business Number (ABN)':
        return 'Format: 11 digits';
      case 'NZBN': // Added NZBN
        return 'Format: 13 digits';
      case 'RCS': // Added RCS
        return 'Luxembourg RCS Number format (e.g., B123456)';
      case 'CVR': // Added CVR
        return 'Format: 8 digits';
      case 'UID': // Added UID
        return 'Format: CHE-XXX.XXX.XXX';
      case 'KvK Number': // Added Netherlands
        return 'Format: 8 digits';
      case 'Handelsregisterauszug': // Added Germany
        return 'Format: HRB XXXXXX (Commercial Register Number)';
      case 'Business Registration Number (BRN) or Unique Business Identifier (UBI)':
        return 'Format: 10-15 alphanumeric characters';
      default:
        return 'Enter the required ID based on your country and business type.';
    }
  }

  // With translations
  switch (businessIdType) {
    case 'SSN':
      return t('businessIdHelpers.ssn');
    case 'Employer Identification Number (EIN)':
      return t('businessIdHelpers.ein');
    case 'Canadian Business Number':
      return t('businessIdHelpers.canadianBusinessNumber');
    case 'Company Number (Great Britain)':
      return t('businessIdHelpers.greatBritain');
    case 'Company Number from ASIC (ACN)':
      return t('businessIdHelpers.asic');
    case 'Australian Business Number (ABN)':
      return t('businessIdHelpers.abn');
    case 'NZBN': // Added NZBN
      return t('businessIdHelpers.nzbn');
    case 'RCS': // Added RCS
      return t('businessIdHelpers.rcs');
    case 'CVR': // Added CVR
      return t('businessIdHelpers.cvr');
    case 'UID': // Added UID
      return t('businessIdHelpers.uid');
    case 'KvK Number': // Added Netherlands
      return t('businessIdHelpers.kvk');
    case 'Handelsregisterauszug': // Added Germany
      return t('businessIdHelpers.handelsregister');
    case 'Business Registration Number (BRN) or Unique Business Identifier (UBI)':
      return t('businessIdHelpers.ubi');
    default:
      return t('businessIdHelpers.default');
  }
};

// Define options before validation schema
export const businessTypeOptions = [
  { label: 'Sole Proprietorship', value: 'Sole Proprietorship' },
  { label: 'Partnership', value: 'Partnership' },
  {
    label: 'Limited Liability Corporation',
    value: 'Limited Liability Corporation',
  },
  { label: 'Co-operative', value: 'Co-operative' },
  { label: 'Non-profit Corporation', value: 'Non-profit Corporation' },
  { label: 'Corporation', value: 'Corporation' },
];

export const countryOptions = [
  { label: 'USA', value: 'USA' },
  { label: 'Canada', value: 'Canada' },
  { label: 'United Kingdom', value: 'United Kingdom' },
  { label: 'Australia', value: 'Australia' },
  { label: 'New Zealand', value: 'New Zealand' },
  { label: 'Luxembourg', value: 'Luxembourg' },
  { label: 'Denmark', value: 'Denmark' },
  { label: 'Switzerland', value: 'Switzerland' },
  { label: 'Netherlands', value: 'Netherlands' },
  { label: 'Germany', value: 'Germany' },
];

// Create arrays of allowed values for validation
const allowedBusinessTypeValues = businessTypeOptions.map((o) => o.value);
const allowedCountryValues = countryOptions.map((o) => o.value);

// Create a validation schema factory function
export const createValidationSchema = (t: any) =>
  Yup.object().shape({
    businessName: Yup.string().required(t('validation.required.businessName')),
    websiteUrl: Yup.string()
      .test('is-valid-url', t('validation.invalidUrl'), (value) => {
        if (!value) return false;
        return validateURL(value);
      })
      .test(
        'not-exercise-domain',
        t('validation.noExerciseDomain'),
        (value) => {
          if (!value) return true;
          return !value.toLowerCase().includes('.exercise.com');
        }
      )
      .required(t('validation.required.websiteUrl')),
    businessType: Yup.string()
      .oneOf(
        allowedBusinessTypeValues, // Use the pre-defined array of values
        t('validation.invalidBusinessType')
      )
      .test(
        'sole-prop-usa-only',
        t('businessTypeInvalidForCountry'),
        function (value) {
          const { country } = this.parent;
          if (value === 'Sole Proprietorship') {
            return country === 'USA';
          }
          return true; // Allow other types for any country
        }
      )
      .required(t('validation.required.businessType')),
    businessIdType: Yup.string()
      // Validate that the type matches the expected one based on country/bizType
      .test(
        'business-id-type-matches-expected',
        t('businessIdTypeError'), // Should not be user-facing if auto-set
        function (value) {
          const { country, businessType } = this.parent;
          const expectedInfo = getExpectedIdInfo(country, businessType);
          // Allow empty string if type is not determined yet
          if (expectedInfo.type === '') return true;
          return value === expectedInfo.type;
        }
      )
      // Optional required validation depending on whether a type is determined
      .when(['country', 'businessType'], {
        is: (country: string, businessType: string) =>
          getExpectedIdInfo(country, businessType).type !== '',
        then: (schema) =>
          schema.required(t('validation.required.businessIdType')),
        otherwise: (schema) => schema.optional(),
      }),
    businessId: Yup.string()
      .test(
        'business-id-format',
        t('businessIdInvalidFormat'),
        function (value) {
          const { businessIdType } = this.parent;
          if (!value || !businessIdType) return true;

          switch (businessIdType) {
            case 'SSN':
              return /^\d{3}-\d{2}-\d{4}$/.test(value);
            case 'Employer Identification Number (EIN)':
              return /^\d{2}-\d{7}$/.test(value);
            case 'Canadian Business Number':
              return /^\d{9}$/.test(value);
            case 'Company Number (Great Britain)':
              return /^[A-Z0-9]{8}$/.test(value);
            case 'Company Number from ASIC (ACN)':
              return /^\d{9}$/.test(value);
            case 'Australian Business Number (ABN)':
              return /^\d{11}$/.test(value);
            case 'NZBN': // Added NZBN validation
              return /^\d{13}$/.test(value);
            case 'RCS': // Added RCS validation
              return /^[A-Z]\d+$/.test(value); // Starts with letter, followed by digits
            case 'CVR': // Added CVR validation
              return /^\d{8}$/.test(value);
            case 'UID': // Added UID validation
              return /^CHE-\d{3}\.\d{3}\.\d{3}$/.test(value);
            case 'KvK Number': // Added Netherlands validation
              return /^\d{8}$/.test(value);
            case 'Handelsregisterauszug': // Added Germany validation
              return /^HRB\s?\d{1,8}$/.test(value);
            case 'Business Registration Number (BRN) or Unique Business Identifier (UBI)':
              return /^[A-Z0-9]{10,15}$/.test(value);
            default:
              return false;
          }
        }
      )
      .when('businessIdType', {
        is: (businessIdType: string) => !!businessIdType,
        then: (schema) => schema.required(t('businessIdRequired')),
        otherwise: (schema) => schema.optional(),
      }),
    regionsOfOperation: Yup.array()
      .of(Yup.string())
      .min(1, t('regions.selectAtLeastOne'))
      .required(
        t(
          'validation.required.regionsOfOperation',
          'Regions of operation are required'
        )
      ),
    jobTitle: Yup.string().required(t('validation.required.jobTitle')),
    jobPosition: Yup.string()
      .oneOf(['Director', 'GM', 'VP', 'CEO', 'CFO', 'General Counsel', 'Other'])
      .required(t('validation.required.jobPosition')),
    firstName: Yup.string().required(t('validation.required.firstName')),
    lastName: Yup.string().required(t('validation.required.lastName')),
    email: Yup.string()
      .email(t('validation.invalidEmail'))
      .required(t('validation.required.email')),
    phoneNumber: Yup.string()
      .required(t('validation.required.phone'))
      .test('phone', t('validation.invalidPhone'), (value) => {
        if (!value) return true;
        return matchIsValidTel(value);
      }),
    streetAddress: Yup.string().required(
      t('validation.required.streetAddress', 'Street address is required')
    ),
    streetAddress2: Yup.string().optional(),
    city: Yup.string().required(t('validation.required.city')),
    state: Yup.string().when('country', {
      // State required only for USA and Canada
      is: (country: string) => country === 'USA' || country === 'Canada',
      then: (schema) => schema.required(t('validation.required.state')),
      otherwise: (schema) => schema.optional(), // Optional for all other countries
    }),
    postalCode: Yup.string()
      .required(t('validation.required.postalCode'))
      .test(
        'valid-postal-code-for-country',
        t('validation.invalidPostalCode'),
        function (value) {
          return validatePostalCode(value, this.parent.country);
        }
      ),
    country: Yup.string()
      .oneOf(
        allowedCountryValues, // Use the pre-defined array of values
        t('validation.invalidCountry')
      )
      .required(t('validation.required.country')),
    areaCode: Yup.string().when('country', {
      // Area code fields only relevant for USA
      is: 'USA',
      then: (schema) =>
        schema
          .matches(/^\d{3}$/, t('validation.maxLength.areaCode'))
          .test('digits-only', t('validation.numbersOnly'), (value) => {
            if (!value) return true; // Handled by required
            return /^\d+$/.test(value);
          })
          .required(t('validation.required.areaCode')),
      otherwise: (schema) => schema.optional(), // Optional and not validated for non-USA
    }),
    areaCodeTwo: Yup.string().when('country', {
      // Area code fields only relevant for USA
      is: 'USA',
      then: (schema) =>
        schema
          .matches(/^\d{3}$/, t('validation.maxLength.areaCode'))
          .test('digits-only', t('validation.numbersOnly'), (value) => {
            if (!value) return true; // Optional field
            return /^\d+$/.test(value);
          }),
      otherwise: (schema) => schema.optional(), // Optional and not validated for non-USA
    }),
  });

export const regionOptions = [
  'Africa',
  'Asia',
  'Europe',
  'Latin America',
  'USA and Canada',
  'Australia',
];

export const jobPositionOptions = [
  { label: 'Director', value: 'Director' },
  { label: 'GM', value: 'GM' },
  { label: 'VP', value: 'VP' },
  { label: 'CEO', value: 'CEO' },
  { label: 'CFO', value: 'CFO' },
  { label: 'General Counsel', value: 'General Counsel' },
  { label: 'Other', value: 'Other' },
];

interface SMSSetupFormProps {
  initialValues: SMSSetupFormValues;
  onSubmit: (values: SMSSetupFormValues) => Promise<void>;
  submitButtonText?: string;
  isSubmitting?: boolean;
  title?: string;
  subtitle?: string;
}

// Inner component containing form fields and logic
const SMSSetupFormFields: React.FC<
  FormikProps<SMSSetupFormValues> & {
    submitButtonText: string;
    isSubmitting: boolean;
    title: string;
    subtitle: string;
  }
> = ({
  values,
  setFieldValue,
  touched,
  errors,
  handleSubmit,
  submitButtonText,
  isSubmitting,
  title,
  subtitle,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'onboarding.sms' });
  const { t: tCommon } = useTranslation('translation', { keyPrefix: 'common' });

  // Determine if state/area codes should be shown
  const showState = values.country === 'USA' || values.country === 'Canada';
  const showAreaCodes = values.country === 'USA';

  // Filter business type options based on country
  const filteredBusinessTypeOptions = useMemo(() => {
    if (values.country === 'USA') {
      return businessTypeOptions;
    }
    return businessTypeOptions.filter(
      (opt) => opt.value !== 'Sole Proprietorship'
    );
  }, [values.country]);

  // Combined Effect to handle field resets/clears based on country/type changes
  useEffect(() => {
    // Reset businessType if country changes from USA and Sole Prop was selected
    if (
      values.country !== 'USA' &&
      values.businessType === 'Sole Proprietorship'
    ) {
      setFieldValue('businessType', '');
      // NOTE: Clearing businessType might trigger the expectedIdInfo recalc and the
      // businessIdType/businessId clearing effect automatically.
    }

    // Clear state if country is not USA or Canada and state has a value
    if (!showState && values.state) {
      setFieldValue('state', '');
    }

    // Clear area codes if country is not USA and they have values
    if (!showAreaCodes) {
      if (values.areaCode) {
        setFieldValue('areaCode', '');
      }
      if (values.areaCodeTwo) {
        setFieldValue('areaCodeTwo', '');
      }
    }
    // Combined dependencies from both original effects
  }, [
    values.country,
    values.businessType,
    showState,
    showAreaCodes,
    setFieldValue,
    values.state,
    values.areaCode,
    values.areaCodeTwo,
  ]);

  // Determine the expected Business ID Type and its Label based on selections
  const expectedIdInfo = useMemo(() => {
    return getExpectedIdInfo(values.country, values.businessType, tCommon);
  }, [values.country, values.businessType, tCommon]);

  // Effect to automatically set the businessIdType field and clear businessId when expected type changes
  useEffect(() => {
    if (expectedIdInfo.type !== values.businessIdType) {
      setFieldValue('businessIdType', expectedIdInfo.type);
      setFieldValue('businessId', '');
    }
  }, [expectedIdInfo.type, values.businessIdType, setFieldValue]);

  // Get postal code helper text dynamically using imported formats
  const postalCodeHelper = useMemo(() => {
    const formatInfo = values.country
      ? postalCodeFormats[values.country]
      : null;
    return formatInfo
      ? `Format: ${formatInfo.example}`
      : t('validation.postalCodeFormat', 'Enter postal code');
  }, [values.country, t]);

  return (
    <StickyCard
      title={title}
      subtitle={subtitle}
      actions={
        <Button
          type="submit"
          variant="prominent"
          color="primary"
          // Disable submit if required ID type isn't determined or if submitting
          disabled={isSubmitting || !values.businessIdType}
          onClick={() => handleSubmit()}
        >
          {isSubmitting ? tCommon('labels.saving') : submitButtonText}
        </Button>
      }
    >
      <Box>
        <Typography sx={{ mb: 3 }}>{t('formIntro')}</Typography>
        <Form>
          <Stack spacing={4}>
            {/* Business Information Section */}
            <Box>
              <Typography variant="h6" gutterBottom>
                {tCommon('labels.businessInformation')}
              </Typography>
              <Stack spacing={3}>
                <FormTextField
                  name="businessName"
                  label={tCommon('fields.businessName')}
                  required
                  fullWidth
                  helperText={t('businessNameHelper')}
                />

                <FormTextField
                  name="websiteUrl"
                  label={tCommon('fields.websiteUrl')}
                  required
                  fullWidth
                />

                <FormTextField
                  name="country"
                  label={tCommon('fields.country')}
                  required
                  fullWidth
                  options={countryOptions}
                />

                <FormTextField
                  name="businessType"
                  label={tCommon('fields.businessType')}
                  required
                  fullWidth
                  options={filteredBusinessTypeOptions}
                />

                {/* Business ID Type field is no longer rendered */}
                {/* Its value is set automatically via useEffect */}

                <FormTextField
                  name="businessId"
                  // Dynamically set label based on the determined type
                  label={expectedIdInfo.label || t('businessIdTypeLabel')}
                  required={!!expectedIdInfo.type} // Required only if a type is expected
                  fullWidth
                  // Dynamically set helper text based on the determined type
                  helperText={getBusinessIdHelperText(expectedIdInfo.type, t)}
                  // Disable if the required type hasn't been determined yet
                  disabled={!expectedIdInfo.type}
                />
              </Stack>
            </Box>

            {/* Regions of Operation Section */}
            <Box>
              <Typography variant="h6" gutterBottom>
                {tCommon('fields.regionsOfOperation')}
              </Typography>
              <FormControl
                error={
                  touched.regionsOfOperation && !!errors.regionsOfOperation
                }
              >
                <FormGroup>
                  {regionOptions.map((region) => (
                    <FormControlLabel
                      key={region}
                      control={
                        <Checkbox
                          checked={values.regionsOfOperation.includes(region)}
                          onChange={(e) => {
                            const isChecked = e.target.checked;
                            const currentRegions = [
                              ...values.regionsOfOperation,
                            ];
                            if (isChecked) {
                              currentRegions.push(region);
                            } else {
                              const index = currentRegions.indexOf(region);
                              if (index > -1) {
                                currentRegions.splice(index, 1);
                              }
                            }
                            setFieldValue('regionsOfOperation', currentRegions);
                          }}
                        />
                      }
                      label={region}
                    />
                  ))}
                </FormGroup>
                {touched.regionsOfOperation && errors.regionsOfOperation && (
                  <FormHelperText>{errors.regionsOfOperation}</FormHelperText>
                )}
              </FormControl>
            </Box>

            {/* Business Representative Section */}
            <Box>
              <Typography variant="h6" gutterBottom>
                {tCommon('labels.businessRepresentative')}
              </Typography>
              <Stack spacing={3}>
                <FormTextField
                  name="jobTitle"
                  label={tCommon('fields.jobTitle')}
                  required
                  fullWidth
                />
                <FormTextField
                  name="jobPosition"
                  label={tCommon('fields.jobPosition')}
                  required
                  fullWidth
                  options={jobPositionOptions}
                />
                <FormTextField
                  name="firstName"
                  label={tCommon('fields.firstName')}
                  required
                  fullWidth
                />
                <FormTextField
                  name="lastName"
                  label={tCommon('fields.lastName')}
                  required
                  fullWidth
                />
                <FormTextField
                  name="email"
                  label={tCommon('fields.email')}
                  type="email"
                  required
                  fullWidth
                />
                <FormPhoneInput
                  name="phoneNumber"
                  label={tCommon('fields.phone')}
                  required
                  fullWidth
                />
              </Stack>
            </Box>

            {/* Business Address Section */}
            <Box>
              <Typography variant="h6" gutterBottom>
                {tCommon('labels.businessAddress')}
              </Typography>
              <Stack spacing={3}>
                <FormTextField
                  name="streetAddress"
                  label={tCommon('fields.addressLine1')}
                  required
                  fullWidth
                />
                <FormTextField
                  name="streetAddress2"
                  label={tCommon('fields.addressLine2')}
                  fullWidth
                />
                <FormTextField
                  name="city"
                  label={tCommon('fields.city')}
                  required
                  fullWidth
                />
                {showState && (
                  <FormTextField
                    name="state"
                    label={tCommon('fields.state')}
                    required
                    fullWidth
                  />
                )}
                <FormTextField
                  name="postalCode"
                  label={tCommon('fields.postalCode')}
                  required
                  fullWidth
                  helperText={postalCodeHelper}
                />
              </Stack>
            </Box>

            {/* Area Code Preferences Section - Conditionally render */}
            {showAreaCodes && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  {tCommon('labels.areaCodePreferences')}
                </Typography>
                <Stack spacing={3}>
                  <FormTextField
                    name="areaCode"
                    label={tCommon('fields.areaCode')}
                    required
                    fullWidth
                  />
                  <FormTextField
                    name="areaCodeTwo"
                    label={tCommon('fields.secondaryAreaCode')}
                    fullWidth
                  />
                </Stack>
              </Box>
            )}
          </Stack>
        </Form>
      </Box>
    </StickyCard>
  );
};

// Main component definition
export const SMSSetupForm: React.FC<SMSSetupFormProps> = ({
  initialValues,
  onSubmit,
  submitButtonText = 'Submit',
  isSubmitting = false,
  title = 'SMS Setup',
  subtitle = 'Please provide your business information for SMS setup',
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'onboarding.sms' });
  const { t: tCommon } = useTranslation('translation', { keyPrefix: 'common' });

  // Create validation schema with translations
  const validationSchema = React.useMemo(() => createValidationSchema(t), [t]);

  // We don't use tCommon directly in this component, but we pass it to child components

  const _unusedVar = tCommon;

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {(formikProps) => (
        // Pass props down to the inner component
        <SMSSetupFormFields
          {...formikProps}
          submitButtonText={submitButtonText}
          isSubmitting={isSubmitting}
          title={title}
          subtitle={subtitle}
        />
      )}
    </Formik>
  );
};
